import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatRippleModule } from '@angular/material/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { NgSelectModule } from '@ng-select/ng-select';
import { EditorModule } from '@tinymce/tinymce-angular';
import {
    NgCapitalizePipeModule,
    NgHeadPipeModule,
    NgJoinPipeModule,
    NgMatchPipeModule,
    NgReplacePipeModule,
    NgTruncatePipeModule,
    NgUpperFirstPipeModule,
} from 'angular-pipes';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { DropzoneModule } from 'ngx-dropzone-wrapper';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { ChatModalMessageComponent } from '../chat/chat-modal/chat-modal-message/chat-modal-message.component';
import { ChatModalComponent } from '../chat/chat-modal/chat-modal.component';
import { ChatEditorComponent } from '../chat/components/chat-editor/chat-editor.component';
import { ControlsModule } from '../controls/controls.module';
import { DirectivesModule } from '../directives/directives.module';
import { EmptyResultComponent } from '../global-search/empty-result/empty-result.component';
import { GlobalSearchOutputComponent } from '../global-search/global-search-output/global-search-output.component';
import { GlobalSearchComponent } from '../global-search/global-search/global-search.component';
import { SearchAccountsViewComponent } from '../global-search/search-accounts-view/search-accounts-view.component';
import { SearchChatsViewComponent } from '../global-search/search-chats-view/search-chats-view.component';
import { SearchHistoryItemComponent } from '../global-search/search-history-item/search-history-item.component';
import { SearchOrderItemComponent } from '../global-search/search-order-item/search-order-item.component';
import { SearchOrderPreviewComponent } from '../global-search/search-order-preview/search-order-preview.component';
import { SearchOrdersViewComponent } from '../global-search/search-orders-view/search-orders-view.component';
import { SearchProductsViewComponent } from '../global-search/search-products-view/search-products-view.component';
import { SearchRecentActionsComponent } from '../global-search/search-recent-actions/search-recent-actions.component';
import { UserAvatarComponent } from '../global-search/user-avatar/user-avatar.component';
import { UserIconComponent } from '../global-search/user-icon/user-icon.component';
import { FloatingSpinnerComponent } from '../notifications/floating-spinner/floating-spinner.component';
import { PipesModule } from '../pipes/pipes.module';
import { AlertComponent } from './alert/alert.component';
import { AvatarComponent } from './avatar/avatar.component';
import { CallAiAssistComponent } from './call-center/call-ai-assist/call-ai-assist.component';
import { CallBarComponent } from './call-center/call-bar/call-bar.component';
import { CallCenterComponent } from './call-center/call-center.component';
import { CallsHistoryComponent } from './call-center/calls-history/calls-history.component';
import { CallsRecordingsComponent } from './call-center/calls-recording/calls-recordings.component';
import { CallsSettingsComponent } from './call-center/calls-settings/calls-settings.component';
import { CallsComponent } from './call-center/calls/calls.component';
import { DialpadComponent } from './call-center/dialpad/dialpad.component';
import { ConfirmationComponent } from './confirmation/confirmation.component';
import { CreateActionFormComponent } from './create-action-form/create-action-form.component';
import { CustomCheckboxComponent } from './custom-checkbox/custom-checkbox.component';
import { FilterControlsComponent } from './filter-controls/filter-controls.component';
import { FooterComponent } from './footer/footer.component';
import { FormErrorsModule } from './form-errors/form-errors.module';
import { HeaderComponent } from './header/header.component';
import { InlineMenuComponent } from './inline-menu/inline-menu.component';
import { InlineSpinnerComponent } from './inline-spinner/inline-spinner.component';
import { MenuComponent } from './menu/menu.component';
import { ModalsModule } from './modals/modals.module';
import { NoteFormComponent } from './note-form/note-form.component';
import { PaginationComponent } from './pagination/pagination.component';
import { SimpleImageViwerComponent } from './simple-image-viwer/simple-image-viwer.component';
import { SmartDateSelectionComponent } from './smart-date-selection/smart-date-selection.component';
import { SmartSearchComponent } from './smart-search/smart-search.component';
import { SpeedDialFabComponent } from './speed-dial-fab/speed-dial-fab.component';
import { SwitcherComponent } from './switcher/switcher.component';
import { TabbedContentComponent } from './tabbed-content/tabbed-content.component';
import { TableComponent } from './table/table.component';
import { TemplatePlaceholderComponent } from './template-placeholder/template-placeholder.component';
import { TimePickerComponent } from './time-picker/time-picker.component';
import { UserCallLogComponent } from './user-call-log/user-call-log.component';
import { VoucherInputComponent } from './voucher-input/voucher-input.component';
import { YSPhoneInputComponent } from './ys-phone-input/ys-phone-input.component';
import { UserSelectorComponent } from '../chat/dialogs/user-selector-dlg/user-selector/user-selector.component';
import { DefaultActionFormComponent } from './create-action-form/subforms/default-action-form/default-action-form.component';
import { CallbackRequestActionFormComponent } from './create-action-form/subforms/callback-request-action-form/callback-request-action-form.component';
import { DatetimePickerComponent } from './components/datetime-picker/datetime-picker.component';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { UserSelectorDropdownComponent } from './components/user-selector-dropdown/user-selector-dropdown.component';
import { MatMenuModule } from '@angular/material/menu';
import { AccountQueryActionFormComponent } from './create-action-form/subforms/account-query-action-form/account-query-action-form.component';
import { CreateCustomerComponent } from './create-action-form/components/create-customer/create-customer.component';
import { UserMultiSelectAreaComponent } from './components/user-multi-select-area/user-multi-select-area.component';
import { OrderQueryActionFormComponent } from './create-action-form/subforms/order-query-action-form/order-query-action-form.component';
import { NewEnquiryActionForm } from './create-action-form/subforms/new-enquiry-action-form/new-enquiry-action-form.component';
import { LabelsModule } from '../labels/labels.module';
import { ActionNoteComponent } from './create-action-form/components/action-note/action-note.component';
import { UserSelectComponent } from '../orders/user-select/user-select.component';
import { UserSelectOrCreateComponent } from './create-action-form/components/user-select-or-create/user-select-or-create.component';
@NgModule({
    declarations: [
        HeaderComponent,
        FooterComponent,
        MenuComponent,
        AlertComponent,
        ConfirmationComponent,
        PaginationComponent,
        CustomCheckboxComponent,
        TableComponent,
        FilterControlsComponent,
        FloatingSpinnerComponent,
        SmartDateSelectionComponent,
        InlineSpinnerComponent,
        SmartSearchComponent,
        GlobalSearchComponent,
        GlobalSearchOutputComponent,
        SearchOrdersViewComponent,
        SearchOrderItemComponent,
        SearchOrderPreviewComponent,
        UserAvatarComponent,
        SearchAccountsViewComponent,
        SearchChatsViewComponent,
        SearchProductsViewComponent,
        SearchRecentActionsComponent,
        SpeedDialFabComponent,
        UserCallLogComponent,
        SearchHistoryItemComponent,
        UserIconComponent,
        EmptyResultComponent,
        TabbedContentComponent,
        InlineMenuComponent,
        ChatModalComponent,
        ChatEditorComponent,
        ChatModalMessageComponent,
        TemplatePlaceholderComponent,
        SwitcherComponent,
        TimePickerComponent,
        CallAiAssistComponent,
        CallCenterComponent,
        DialpadComponent,
        CallsComponent,
        CallBarComponent,
        CallsHistoryComponent,
        CallsRecordingsComponent,
        CallsSettingsComponent,
        NoteFormComponent,
        AvatarComponent,
        SimpleImageViwerComponent,
        CreateActionFormComponent,
        VoucherInputComponent,
        YSPhoneInputComponent,
        UserSelectorComponent,
        DefaultActionFormComponent,
        CallbackRequestActionFormComponent,
        DatetimePickerComponent,
        UserSelectorDropdownComponent,
        AccountQueryActionFormComponent,
        OrderQueryActionFormComponent,
        NewEnquiryActionForm,
        CreateCustomerComponent,
        UserMultiSelectAreaComponent,
        ActionNoteComponent,
        UserSelectComponent,
        UserSelectOrCreateComponent,
    ],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        CKEditorModule,
        AngularSvgIconModule,
        DirectivesModule,
        PipesModule,
        BsDatepickerModule,
        BsDropdownModule,
        MatDialogModule,
        NgSelectModule,
        MatCardModule,
        NgxDaterangepickerMd.forRoot(),
        TimepickerModule.forRoot(),
        PopoverModule,
        MatSidenavModule,
        ModalsModule,
        MatProgressSpinnerModule,
        ControlsModule,
        NgReplacePipeModule,
        NgMatchPipeModule,
        NgHeadPipeModule,
        MatButtonModule,
        MatTooltipModule,
        MatIconModule,
        ScrollingModule,
        ScrollingModule,
        FormErrorsModule,
        DragDropModule,
        MatRippleModule,
        NgCapitalizePipeModule,
        NgTruncatePipeModule,
        NgUpperFirstPipeModule,
        CollapseModule,
        DropzoneModule,
        EditorModule,
        NgJoinPipeModule,
        NgxIntlTelInputModule,
        MatMenuModule,
        LabelsModule,
    ],
    exports: [
        HeaderComponent,
        FooterComponent,
        FormsModule,
        ReactiveFormsModule,
        MenuComponent,
        TabbedContentComponent,
        AlertComponent,
        ConfirmationComponent,
        PaginationComponent,
        CKEditorModule,
        CustomCheckboxComponent,
        TableComponent,
        FilterControlsComponent,
        FloatingSpinnerComponent,
        MatDialogModule,
        MatTooltipModule,
        SmartDateSelectionComponent,
        InlineSpinnerComponent,
        UserAvatarComponent,
        SpeedDialFabComponent,
        UserCallLogComponent,
        ModalsModule,
        MatIconModule,
        UserIconComponent,
        InlineMenuComponent,
        UserIconComponent,
        FormErrorsModule,
        ChatModalComponent,
        ChatEditorComponent,
        ChatModalMessageComponent,
        TemplatePlaceholderComponent,
        SwitcherComponent,
        TimePickerComponent,
        DirectivesModule,
        EmptyResultComponent,
        NoteFormComponent,
        AvatarComponent,
        SimpleImageViwerComponent,
        CallsHistoryComponent,
        VoucherInputComponent,
        YSPhoneInputComponent,
        UserSelectorComponent,
        UserSelectComponent,
    ],
    providers: [
        {
            provide: MatDialogRef,
            useValue: MatDialogRef,
        },
    ],
})
export class SharedModule {}
